import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { 
  ArrowLeft, Shield, FileText, Lock, Eye, Scale, 
  UserCheck, Server, Globe, AlertTriangle, Mail,
  ChevronDown, ChevronRight, Clock, CreditCard,
  ShieldCheck, CheckCircle2, ArrowRight
} from 'lucide-react';
import PageTransition from '@/components/global/PageTransition';
import SEO from '@/components/global/SEO';
import RasterGridBackdrop from '@/components/ui/RasterGridBackdrop';
import EditorialHero from '@/components/ui/EditorialHero';
import { Language } from '@/types';

interface TermsPoliciesProps {
  lang: Language;
}

export const TermsPolicies: React.FC<TermsPoliciesProps> = ({ lang }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isRtl = lang === 'ar';
  const isAr = isRtl;

  const initialTab = searchParams.get('tab') || 'terms';
  const [activeSection, setActiveSection] = useState<string | null>(initialTab);

  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam && ['terms', 'privacy', 'usage', 'refund', 'security'].includes(tabParam)) {
      setActiveSection(tabParam);
    }
  }, [searchParams]);

  const handleTabChange = (sectionId: string) => {
    const newTab = activeSection === sectionId ? null : sectionId;
    setActiveSection(newTab);
    if (newTab) {
      setSearchParams({ tab: newTab });
    } else {
      setSearchParams({});
    }
  };

  const sections = [
    {
      id: 'terms',
      icon: Scale,
      title: { en: 'Terms of Service', ar: 'شروط الخدمة' },
      badge: 'EGIPA #5007',
      content: {
        en: [
          {
            heading: 'Acceptance of Terms',
            text: 'By accessing and using VEXL Spatial AI platform ("Service"), including VEXL Nova OS, GeoAgent, CropLens, DroughtWatch, and desktop API interfaces, you agree to be bound by these Terms. If you do not agree to these terms, please do not use our Service.'
          },
          {
            heading: 'Description of Service',
            text: 'VEXL provides an enterprise spatial intelligence operating system that empowers GIS professionals and institutions to generate ArcPy scripts, analyze multi-spectral satellite imagery, validate geodetic coordinate transformations, and automate sovereign geospatial workflows.'
          },
          {
            heading: 'User Accounts & Access',
            text: 'You are responsible for maintaining the confidentiality of your credentials and API keys. You agree to provide accurate information during institutional onboarding and maintain secure access controls across your team.'
          },
          {
            heading: 'Acceptable Use & System Integrity',
            text: 'You agree not to use the Service for illegal activities, reverse-engineering AI model weights, extracting protected algorithms, bypassing compute rate limits, or launching automated attacks against platform infrastructure.'
          },
          {
            heading: 'Intellectual Property & Certified Software Deposit',
            text: 'The VEXL Ecosystem, its tensor-topology algorithms, and UI runtimes are protected under international copyright and officially registered with the Egyptian Intellectual Property Authority (EGIPA Deposit No. 5007). You retain exclusive ownership of all custom ArcPy scripts and spatial vector outputs you create.'
          },
          {
            heading: 'Service Level & Compute Modifications',
            text: 'We maintain a 99.9% uptime target for enterprise workloads. Pricing adjustments or major compute quota changes are communicated with 30-day advance notice to contracted organizations.'
          },
          {
            heading: 'Termination & Data Portability',
            text: 'Either party may terminate service access. Upon termination, users have 30 days to export their spatial project histories and analytical reports before cryptographic purge.'
          }
        ],
        ar: [
          {
            heading: 'قبول الشروط والاتفاقية',
            text: 'باستخدامك لمنصة VEXL للذكاء الاصطناعي الجيومكاني، بما في ذلك نظام تشغيل VEXL Nova، وGeoAgent، وCropLens، وDroughtWatch، وواجهات الربط البرمجي، فإنك توافق على الالتزام بهذه الشروط والأحكام.'
          },
          {
            heading: 'وصف الخدمة والمنظومة',
            text: 'توفر VEXL نظام تشغيل مكانياً متطوراً يساعد المتخصصين والمؤسسات في إنشاء نصوص ArcPy، وتحليل صور الأقمار الصناعية، وتدقيق أنظمة الإسناد المكاني، وأتمتة مسارات العمل الجيوديسية السيادية.'
          },
          {
            heading: 'الحسابات والوصول المؤسسي',
            text: 'أنت مسؤول عن الحفاظ على سرية بيانات الاعتماد ومفاتيح الربط البرمجي (API Keys) الخاصة بحسابك، والالتزام بضوابط الوصول الآمن لفريق عملك.'
          },
          {
            heading: 'الاستخدام المقبول وسلامة المنظومة',
            text: 'توافق على عدم استخدام الخدمة في أي أنشطة غير قانونية، أو محاولة الهندسة العكسية لأوزان النماذج الذكية، أو محاولة تجاوز حصص الحوسبة المكانية، أو شن هجمات برمجية.'
          },
          {
            heading: 'الملكية الفكرية والإيداع القانوني',
            text: 'المنظومة محمية قانونياً ومسجلة رسمياً لدى الجهاز المصري للملكية الفكرية برقم إيداع (5007). يحتفظ العميل بالملكية الحصرية لكافة نصوص ArcPy والمخرجات المكانية المشتقة.'
          },
          {
            heading: 'مستوى الخدمة وتعديل الحصص',
            text: 'نلتزم بمستوى جاهزية 99.9% للأعمال المؤسسية. ويتم إخطار الجهات المتعاقدة مسبقاً قبل 30 يوماً بأي تعديل في حصص الحوسبة المكانية.'
          },
          {
            heading: 'إنهاء الخدمة وتصدير البيانات',
            text: 'يحق لأي من الطرفين إنهاء الخدمة. ويُمنح المستخدم مهلة 30 يوماً لتصدير سجلات مشاريعه المكانية وتقاريره قبل التطهير التشفيري للبيانات.'
          }
        ]
      }
    },
    {
      id: 'privacy',
      icon: Eye,
      title: { en: 'Privacy & Data Sovereignty', ar: 'الخصوصية وسيادة البيانات' },
      badge: 'Zero-Training',
      content: {
        en: [
          {
            heading: 'Zero Customer Training Guarantee',
            text: 'We categorically do not use your confidential coordinate points, polygon shapes, CAD boundaries, or satellite raster scenes to train, fine-tune, or validate any commercial foundation models.'
          },
          {
            heading: 'Ephemeral Memory Sandboxing',
            text: 'Spatial data ingested during interactive analysis is processed in volatile memory enclaves and permanently expunged upon session termination or project archival.'
          },
          {
            heading: 'Air-Gapped Sovereign Readiness',
            text: 'For government entities and defense clients, VEXL provides completely air-gapped on-premise deployments with zero outbound network egress.'
          },
          {
            heading: 'Statutory Data Protection Compliance',
            text: 'Our processing workflows comply with the Egyptian Personal Data Protection Law (Law No. 151 of 2020) and international data governance frameworks.'
          }
        ],
        ar: [
          {
            heading: 'ضمان انعدام التدريب التام',
            text: 'نلتزم بشكل قاطع بعدم استخدام إحداثياتك أو مضلعاتك المكانية أو صور الأقمار الصناعية في تدريب أو ضبط أي نماذج ذكاء اصطناعي.'
          },
          {
            heading: 'معالجة آنية في الذاكرة الحية',
            text: 'تتم معالجة البيانات المكانية المدخلة في الذاكرة الحية فقط ويتم تطهيرها ومسحها فور انتهاء الجلسة أو أرشفة المشروع.'
          },
          {
            heading: 'النشر المعزول شبكياً (Air-Gap)',
            text: 'للجهات الحكومية والسيادية، نوفر خيارات نشر محلي معزول تماماً دون أي اتصال شبكي خارجي لضمان السيادة المطلقة.'
          },
          {
            heading: 'الامتثال لقانون حماية البيانات',
            text: 'تتوافق كافة مسارات العمل مع قانون حماية البيانات الشخصية المصري رقم 151 لسنة 2020 والمعايير الدولية.'
          }
        ]
      }
    },
    {
      id: 'usage',
      icon: Server,
      title: { en: 'Spatial Compute Policy', ar: 'سياسة الحوسبة المكانية' },
      badge: 'Quota & Concurrency',
      content: {
        en: [
          {
            heading: 'Compute Quota Allocation',
            text: 'Accounts receive tiered spatial compute units (SCUs) allocated monthly based on subscription tier for satellite raster processing, DEM derivation, and geodetic transforms.'
          },
          {
            heading: 'Concurrency & Rate Limiting',
            text: 'To guarantee operational stability across the network, API calls are rate-limited per organization. Enterprise tiers receive dedicated high-performance GPU/vCPU clusters.'
          },
          {
            heading: 'Automated Scripting Audits',
            text: 'Automated high-frequency batch requests must utilize standard pagination and backoff mechanisms to prevent systemic resource contention.'
          },
          {
            heading: 'High-Volume Enterprise Workloads',
            text: 'Organizations requiring petabyte-scale spatial analytics can provision private isolated clusters with customizable hardware configurations.'
          }
        ],
        ar: [
          {
            heading: 'تخصيص وحدات الحوسبة المكانية',
            text: 'تحصل الحسابات على وحدات حوسبة مكانية (SCUs) شهرية مخصصة لمعالجة صور الأقمار الصناعية، وتحليلات الارتفاعات الرقمية، والتحويلات الجيوديسية.'
          },
          {
            heading: 'معدلات الاستخدام والتزامن',
            text: 'لضمان الاستقرار التشغيلي، تخضع استدعاءات API لضوابط تزامن ومعدلات محددة. وتحصل المؤسسات على خوادم مخصصة ذات أداء فائق.'
          },
          {
            heading: 'أتمتة الاستدعاءات وسير العمل',
            text: 'يجب أن تتبع استدعاءات المعالجة الدفعية الكبيرة آليات التراجع التدريجي القياسية لمنع الضغط المفرط على موارد الشبكة.'
          },
          {
            heading: 'أحجام العمل المؤسسية الضخمة',
            text: 'يمكن للمؤسسات التي تتطلب معالجة بيتابايت من البيانات المكانية حجز خوادم معزولة ذات مواصفات عتادية مخصصة.'
          }
        ]
      }
    },
    {
      id: 'refund',
      icon: CreditCard,
      title: { en: 'Subscription & Commercial Terms', ar: 'شروط الاشتراكات والفوترة' },
      badge: 'Billing & SLA',
      content: {
        en: [
          {
            heading: 'Transparent Billing Cadence',
            text: 'Subscriptions renew automatically on a monthly or annual cycle. Invoices and VAT receipts are issued through accredited financial channels.'
          },
          {
            heading: '30-Day Satisfaction Guarantee',
            text: 'New individual and academic subscribers may request a full refund within 30 days of initial onboarding if the spatial toolchain does not meet operational expectations.'
          },
          {
            heading: 'Enterprise Contracts & Purchase Orders',
            text: 'Institutional and government purchase orders are governed by tailored commercial agreements formalized under a Master Services Agreement (MSA).'
          },
          {
            heading: 'Refund Processing Timeline',
            text: 'Approved refunds are credited back to the original payment method within 5 to 10 business days depending on banking clearing channels.'
          }
        ],
        ar: [
          {
            heading: 'دورية الفوترة الشفافة',
            text: 'تتجدد الاشتراكات تلقائياً بشكل شهري أو سنوي. وتصدر الفواتير الضريبية المعتمدة عبر القنوات المالية النظامية.'
          },
          {
            heading: 'ضمان الاسترداد خلال 30 يوماً',
            text: 'يحق للمشتركين الجدد في الباقات الفردية والأكاديمية طلب استرداد كامل للمبلغ خلال 30 يوماً في حال عدم تلبية المنظومة لتوقعاتهم.'
          },
          {
            heading: 'العقود المؤسسية وأوامر الشراء',
            text: 'تخضع عقود المؤسسات الحكومية والشركات وأوامر التوريد لشروط تعاقدية مخصصة ومحددة في اتفاقية تقديم الخدمات الرئيسية (MSA).'
          },
          {
            heading: 'فترة معالجة الاسترداد',
            text: 'تتم معالجة مبالغ الاسترداد المعتمدة وإعادتها إلى وسيلة الدفع الأصلية خلال 5 إلى 10 أيام عمل وفق القنوات المصرفية المعتمدة.'
          }
        ]
      }
    },
    {
      id: 'security',
      icon: Lock,
      title: { en: 'Security Architecture Policy', ar: 'سياسة معمارية الأمان' },
      badge: 'SOC 2 & Air-Gap',
      content: {
        en: [
          {
            heading: 'Cryptographic Standards',
            text: 'End-to-end data encryption using TLS 1.3 with forward secrecy for all API traffic, and AES-256 for persistent database storage.'
          },
          {
            heading: 'Cloud & Infrastructure Audits',
            text: 'Hosted on top-tier cloud infrastructure compliant with SOC 2 Type II and ISO 27001 certifications. Routine third-party penetration testing and automated vulnerability auditing.'
          },
          {
            heading: 'Access Controls & SSO',
            text: 'Strict Role-Based Access Control (RBAC), multi-factor authentication (MFA/2FA), and SAML 2.0 / OAuth enterprise single sign-on.'
          },
          {
            heading: 'Responsible Vulnerability Disclosure',
            text: 'We welcome reports from independent security researchers. Vulnerabilities can be reported to vexl.gis@gmail.com with a 48-hour response triage SLA.'
          }
        ],
        ar: [
          {
            heading: 'معايير التشفير المؤسسي',
            text: 'تشفير شامل للبيانات أثناء النقل ببروتوكول TLS 1.3 مع سرية أمامية، وتشفير البيانات المخزنة بمعيار AES-256 المعتمد دولياً.'
          },
          {
            heading: 'تدقيق البنية التحتية والسحابة',
            text: 'تتم الاستضافة على مراكز بيانات حاصلة على شهادات SOC 2 Type II وISO 27001، مع تدقيق أمني واختبارات اختراق دورية مؤتمتة.'
          },
          {
            heading: 'ضوابط الوصول وتسجيل الدخول الموحد',
            text: 'تطبيق قواعد الوصول المستند إلى الأدوار (RBAC)، والمصادقة متعددة العوامل (MFA)، ودعم تسجيل الدخول المؤسسي الموحد (SSO).'
          },
          {
            heading: 'برنامج الإفصاح المسؤول عن الثغرات',
            text: 'نرحب بتقارير الباحثين الأمنيين. يمكن الإبلاغ عن أي ملاحظات أمنية عبر vexl.gis@gmail.com مع التزام بالرد خلال 48 ساعة.'
          }
        ]
      }
    }
  ];

  return (
    <PageTransition>
      <SEO 
        title={isAr ? 'مركز السياسات والحوكمة المكانية | منظومة فكسل' : 'Policies & Governance Hub | VEXL Ecosystem'}
        description={
          isAr 
            ? 'المركز الشامل لسياسات فكسل: شروط الاستخدام، الخصوصية، الحوسبة المكانية، الفوترة، ومعايير الأمان.'
            : 'Unified policies hub for VEXL: Terms of Service, Privacy Policy, Compute Quotas, Refund, and Security Architecture.'
        }
        lang={lang}
      />

      <div 
        className={`min-h-screen bg-[#05070A] text-[#EDF0F1] relative overflow-hidden selection:bg-[#0E93A3]/30 ${
          isRtl ? 'font-arabic text-right' : 'font-sans text-left'
        }`} 
        dir={isRtl ? 'rtl' : 'ltr'}
      >
        <RasterGridBackdrop type="type-a" />

        <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 pt-20 md:pt-24 pb-24">
          
          {/* Canonical Two-Column Editorial Split Hero */}
          <EditorialHero
            showBackButton={true}
            backButtonLabel={isAr ? 'العودة إلى البوابة الرئيسية' : 'Return to Home'}
            onBackClick={() => navigate('/')}
            title={isAr ? 'السياسات والشروط المعتمدة' : 'Unified Policies & Governance Hub'}
            description={
              isAr 
                ? 'الدليل الشامل لكافة الوثائق التنظيمية، والضوابط التشغيلية، ومحددات الحوسبة المكانية، وضمانات الخصوصية والأمان لمنظومة فكسل.'
                : 'The comprehensive catalog of regulatory frameworks, operational directives, spatial compute constraints, and sovereign trust policies.'
            }
            actions={[
              { label: isAr ? 'شروط الخدمة' : 'Terms of Service', href: '/terms', variant: 'secondary' },
              { label: isAr ? 'وثيقة الخصوصية' : 'Privacy Policy', href: '/privacy', variant: 'secondary' }
            ]}
            isAr={isAr}
            className="mb-10"
          />

          {/* Tab Selection Bar */}
          <div className="flex flex-wrap justify-center gap-2 mb-10">
            {sections.map((section) => {
              const Icon = section.icon;
              const isSelected = activeSection === section.id;

              return (
                <button
                  key={section.id}
                  onClick={() => handleTabChange(section.id)}
                  className={`flex items-center gap-2.5 px-5 py-2.5 rounded-xl text-xs sm:text-sm font-medium transition-all ${
                    isSelected
                      ? 'bg-[#0E93A3] text-black font-bold shadow-[0_0_20px_rgba(14,147,163,0.35)]'
                      : 'bg-[#12161A]/90 border border-[#262C31] text-[#94A3B8] hover:text-white hover:bg-[#181E24]'
                  }`}
                >
                  <Icon size={16} className={isSelected ? 'text-black' : 'text-[#0E93A3]'} />
                  <span>{section.title[lang]}</span>
                  {isSelected ? <ChevronDown size={14} /> : <ChevronRight size={14} className={isRtl ? 'rotate-180' : ''} />}
                </button>
              );
            })}
          </div>

          {/* Active Section Content */}
          <div className="max-w-4xl mx-auto space-y-6">
            <AnimatePresence mode="wait">
              {sections.map((section) => {
                const Icon = section.icon;
                if (activeSection !== section.id) return null;

                return (
                  <motion.div
                    key={section.id}
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -15 }}
                    transition={{ duration: 0.25 }}
                    className="p-6 sm:p-10 rounded-2xl bg-[#12161A]/90 border border-[#262C31] backdrop-blur-md shadow-2xl"
                  >
                    {/* Section Header */}
                    <div className="flex items-center justify-between gap-4 mb-8 pb-6 border-b border-[#262C31]">
                      <div className="flex items-center gap-3.5">
                        <div className="p-3 rounded-xl bg-[#0E93A3]/10 border border-[#0E93A3]/25 text-[#0E93A3]">
                          <Icon size={22} />
                        </div>
                        <div>
                          <h2 className="text-xl sm:text-2xl font-bold text-white">
                            {section.title[lang]}
                          </h2>
                        </div>
                      </div>
                    </div>

                    {/* Clauses */}
                    <div className="space-y-6">
                      {section.content[lang].map((item, idx) => (
                        <div
                          key={idx}
                          className="p-4 sm:p-5 rounded-xl bg-[#0B0E12] border border-[#262C31]/70 hover:border-[#262C31] transition-all"
                        >
                          <h3 className="text-sm sm:text-base font-bold text-white mb-2 flex items-center gap-2">
                            <span className="text-[#0E93A3] font-mono text-xs font-semibold">
                              #{idx + 1}
                            </span>
                            {item.heading}
                          </h3>
                          <p className="text-[#94A3B8] text-xs sm:text-sm leading-relaxed">
                            {item.text}
                          </p>
                        </div>
                      ))}
                    </div>

                    {/* Context Links */}
                    <div className="mt-8 pt-6 border-t border-[#262C31] flex flex-wrap items-center justify-between gap-4">
                      <p className="text-xs text-[#64748B]">
                        {isAr ? 'للاطلاع على النص الكامل المفصل:' : 'View the dedicated standalone documentation:'}
                      </p>
                      <div className="flex items-center gap-3">
                        {section.id === 'terms' && (
                          <Link
                            to="/terms"
                            className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#0E93A3] hover:underline"
                          >
                            <span>{isAr ? 'صفحة شروط الخدمة المستقلة' : 'Full Terms of Service Page'}</span>
                            <ArrowRight size={13} className={isRtl ? 'rotate-180' : ''} />
                          </Link>
                        )}
                        {section.id === 'privacy' && (
                          <Link
                            to="/privacy"
                            className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#0E93A3] hover:underline"
                          >
                            <span>{isAr ? 'صفحة الخصوصية المستقلة' : 'Full Privacy Policy Page'}</span>
                            <ArrowRight size={13} className={isRtl ? 'rotate-180' : ''} />
                          </Link>
                        )}
                        {section.id === 'security' && (
                          <Link
                            to="/security"
                            className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#0E93A3] hover:underline"
                          >
                            <span>{isAr ? 'بوابة الأمان والسيادة' : 'Full Security Architecture Page'}</span>
                            <ArrowRight size={13} className={isRtl ? 'rotate-180' : ''} />
                          </Link>
                        )}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>

            {!activeSection && (
              <div className="text-center py-16 p-8 rounded-2xl bg-[#12161A]/40 border border-[#262C31]">
                <p className="text-[#94A3B8] text-sm mb-4">
                  {isAr ? 'اختر قسماً من الأزرار أعلاه لقراءة بنود السياسة المحددة.' : 'Select a section above to view the policy clauses.'}
                </p>
                <button
                  onClick={() => setActiveSection('terms')}
                  className="px-4 py-2 rounded-full bg-[#0E93A3] text-black text-xs font-bold"
                >
                  {isAr ? 'عرض شروط الخدمة' : 'View Terms of Service'}
                </button>
              </div>
            )}
          </div>

          {/* Legal Help Desk Contact Block */}
          <div className="mt-16 text-center max-w-xl mx-auto p-8 rounded-2xl bg-[#12161A]/60 border border-[#262C31]">
            <h4 className="text-base font-bold text-white mb-2">
              {isAr ? 'هل تحتاج إلى استفسار قانوني أو اتفاقية مخصصة؟' : 'Need Legal Clarification or a Custom DPA?'}
            </h4>
            <p className="text-xs text-[#94A3B8] mb-5 leading-relaxed">
              {isAr 
                ? 'فريق الحوكمة القانونية والامتثال مستعد للإجابة على استفسارات المؤسسات والجهات الشريكة.'
                : 'Our legal and compliance team is available to support institutional partners and enterprise clients.'}
            </p>
            <a
              href="mailto:vexl.gis@gmail.com"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-white/[0.04] border border-[#262C31] hover:border-[#0E93A3] text-white hover:text-[#0E93A3] text-xs font-mono font-medium transition-all"
            >
              <Mail size={15} />
              vexl.gis@gmail.com
            </a>
          </div>

          {/* Footer Note */}
          <div className="mt-14 pt-8 border-t border-[#262C31]/70 text-center">
            <p className="text-[#64748B] text-xs font-mono">
              {isAr ? 'منظومة VEXL © 2026. مسجلة رسمياً برقم إيداع 5007 (EGIPA). جميع الحقوق محفوظة.' : 'VEXL Ecosystem © 2026. Registered under EGIPA Deposit #5007. All Rights Reserved.'}
            </p>
          </div>

        </div>
      </div>
    </PageTransition>
  );
};

export default TermsPolicies;
